import { Injectable, inject } from '@angular/core';
import {
  NzNotificationService,
  NzNotificationRef,
} from 'ng-zorro-antd/notification';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private notification = inject(NzNotificationService);
  private activeNotifications = new Map<string, NzNotificationRef>();

  /**
   * Show a notification with automatic duplicate prevention
   * @param type - notification type (success, error, warning, info)
   * @param title - notification title
   * @param content - notification content
   * @param key - unique key to prevent duplicates (optional)
   * @param options - additional notification options
   */
  create(
    type: 'success' | 'error' | 'warning' | 'info',
    title: string,
    content: string,
    key?: string,
    options?: any,
  ): NzNotificationRef {
    // If key is provided and notification already exists, remove the old one
    if (key && this.activeNotifications.has(key)) {
      this.remove(this.activeNotifications.get(key)!);
    }

    // Placement, duration and stacking come from the global ng-zorro config.
    const notificationRef = this.notification.create(
      type,
      title,
      content,
      options,
    );

    // Store the notification reference with its key for future reference
    if (key) {
      this.activeNotifications.set(key, notificationRef);
    }

    return notificationRef;
  }

  /**
   * Remove a specific notification by reference
   */
  remove(notificationRef: NzNotificationRef): void {
    this.notification.remove(notificationRef.messageId);

    // Remove from active notifications map
    for (const [key, ref] of this.activeNotifications.entries()) {
      if (ref.messageId === notificationRef.messageId) {
        this.activeNotifications.delete(key);
        break;
      }
    }
  }

  /**
   * Remove notification by key
   */
  removeByKey(key: string): void {
    const notificationRef = this.activeNotifications.get(key);
    if (notificationRef) {
      this.remove(notificationRef);
    }
  }

  /**
   * Clear all notifications
   */
  clearAll(): void {
    // Clear all notifications using ng-zorro's remove method without parameters
    this.notification.remove();
    this.activeNotifications.clear();
  }

  /**
   * Clear notifications by type
   */
  clearByType(type: 'success' | 'error' | 'warning' | 'info'): void {

    this.clearAll();
  }

  /**
   * Clear authentication-related notifications
   */
  clearAuthNotifications(): void {
    // Remove common auth notification keys
    const authKeys = [
      'login-error',
      'auth-failed',
      'invalid-credentials',
      'email-not-verified',
      'awaiting-approval',
    ];

    authKeys.forEach((key) => this.removeByKey(key));
  }
}
